import { useEffect, useLayoutEffect, useCallback, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useAuth } from '@clerk/clerk-react'
import { setAuthTokenGetter, setSessionIdGetter } from '../lib/auth/tokenBridge'
import { resetAnalytics } from '../lib/analytics'

type AccessTokenPayload = Record<string, unknown>

const clerkJwtTemplate = import.meta.env.VITE_CLERK_JWT_TEMPLATE?.trim() || null

interface SessionState {
  isLoading: boolean
  isAuthenticated: boolean
  isTokenReady: boolean
  userId: string | null
  sessionId: string | null
  accessTokenPayload: AccessTokenPayload | null
}

const TOKEN_READY_RETRY_DELAYS_MS = [150, 400, 900]

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

function setSentrySessionTag(sessionId: string | null) {
  Sentry.setTag('session_id', sessionId ?? 'none')
}

/**
 * useSession
 *
 * Returns the current authentication session state.
 *
 * @returns {{
 *   isLoading: boolean,
 *   isAuthenticated: boolean,
 *   userId: string | null,
 *   sessionId: string | null,
 *   accessTokenPayload: object | null,
 *   signOut: () => Promise<void>,
 * }}
 *
 * Usage:
 *   const { isLoading, isAuthenticated, userId, signOut } = useSession()
 */
export function useSession(): SessionState & { signOut: () => Promise<void> } {
  const { isLoaded, isSignedIn, userId, sessionId, sessionClaims, getToken, signOut } = useAuth()
  const [isTokenReady, setIsTokenReady] = useState(false)

  useEffect(() => {
    setSentrySessionTag(sessionId ?? null)
  }, [sessionId])

  useLayoutEffect(() => {
    setAuthTokenGetter(async () => {
      if (!isLoaded || !getToken) return null
      const token = clerkJwtTemplate
        ? await getToken({ template: clerkJwtTemplate })
        : await getToken()
      return token ?? null
    })

    setSessionIdGetter(() => sessionId ?? null)
    // Keep the bridge registered to avoid transient gaps between effect cleanup/setup cycles.
  }, [getToken, isLoaded, sessionId])

  useEffect(() => {
    let cancelled = false

    const setTokenReadySafely = (value: boolean) => {
      queueMicrotask(() => {
        if (!cancelled) {
          setIsTokenReady(value)
        }
      })
    }

    if (!isLoaded) {
      setTokenReadySafely(false)
      return
    }

    if (!isSignedIn) {
      setTokenReadySafely(true)
      return
    }

    if (!getToken) {
      setTokenReadySafely(false)
      return
    }

    setTokenReadySafely(false)

    const resolveTokenReadiness = async () => {
      const maxAttempts = TOKEN_READY_RETRY_DELAYS_MS.length + 1

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
          const token = clerkJwtTemplate
            ? await getToken({ template: clerkJwtTemplate })
            : await getToken()

          if (cancelled) return

          if (token) {
            setIsTokenReady(true)
            return
          }
        } catch {
          // Keep retrying. Request-time auth fetch can still recover.
        }

        if (attempt < TOKEN_READY_RETRY_DELAYS_MS.length) {
          await wait(TOKEN_READY_RETRY_DELAYS_MS[attempt])
          if (cancelled) return
        }
      }

      if (!cancelled) {
        // Do not block app forever if warm-up token fetch fails.
        setIsTokenReady(true)
      }
    }

    void resolveTokenReadiness()

    return () => {
      cancelled = true
    }
  }, [getToken, isLoaded, isSignedIn, sessionId])

  const handleSignOut = useCallback(async () => {
    if (signOut) {
      await signOut()
    }
    setSentrySessionTag(null)
    resetAnalytics()
  }, [signOut])

  return {
    isLoading: !isLoaded,
    isAuthenticated: isSignedIn ?? false,
    isTokenReady,
    userId: userId ?? null,
    sessionId: sessionId ?? null,
    accessTokenPayload: (sessionClaims ?? null) as AccessTokenPayload | null,
    signOut: handleSignOut,
  }
}
